import React, { useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaArrowRight, FaEnvelope, FaPhone, FaMapMarkerAlt, FaUser, FaClock, FaStore } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fetchPharmacyBranchDetails, clearError, clearBranchDetails } from '../../../redux/Slices/AdminPharmaciesSlice';

const AdminPharmacyBranchDetails = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { pharmacyId, branchId } = useParams();
    
    const { branchDetails, branchDetailsLoading, error } = useSelector((state) => state.adminPharmacies);

    useEffect(() => {
        setTitle('تفاصيل الفرع');
        dispatch(fetchPharmacyBranchDetails({ pharmacyId, branchId }));
        
        return () => {
            dispatch(clearBranchDetails());
        };
    }, [setTitle, dispatch, pharmacyId, branchId]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const getStatusBadge = (status) => {
        if (status === "1" || status === 1) {
            return <span className="badge bg-success">نشط</span>;
        }
        return <span className="badge bg-danger">غير نشط</span>;
    };

    const getBanBadge = (ban) => {
        if (ban === "1" || ban === 1) {
            return <span className="badge bg-danger">محظور</span>;
        }
        return <span className="badge bg-success">غير محظور</span>;
    };

    if (branchDetailsLoading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل تفاصيل الفرع...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!branchDetails) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <p className="text-muted">لا توجد بيانات</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">تفاصيل الفرع</h4>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn text-white"
                        onClick={() => navigate(`/admin-pharmacies/${pharmacyId}/branches`)}
                        style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                        <FaArrowRight className="mx-2" />
                        العودة للفروع
                    </motion.button>
                </div>

                {/* Branch Info Card */}
                <div className="card shadow-lg border-0 mb-4">
                    <div className="card-body p-4">
                        <div className="row">
                            {/* Logo and Cover */}
                            <div className="col-md-12 mb-4">
                                <div className="row">
                                    <div className="col-md-6">
                                        <h6 className="text-muted mb-2">اللوجو</h6>
                                        <div style={{ width: '150px', height: '150px', border: '2px dashed #ddd', borderRadius: '10px' }}>
                                            {branchDetails.logo ? (
                                                <img
                                                    src={branchDetails.logo}
                                                    alt="Logo"
                                                    className="w-100 h-100"
                                                    style={{ objectFit: 'cover', borderRadius: '10px' }}
                                                />
                                            ) : (
                                                <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                                    لا يوجد لوجو
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <h6 className="text-muted mb-2">الغلاف</h6>
                                        <div style={{ width: '100%', height: '150px', border: '2px dashed #ddd', borderRadius: '10px' }}>
                                            {branchDetails.cover ? (
                                                <img
                                                    src={branchDetails.cover}
                                                    alt="Cover"
                                                    className="w-100 h-100"
                                                    style={{ objectFit: 'cover', borderRadius: '10px' }}
                                                />
                                            ) : (
                                                <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                                    لا يوجد غلاف
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* عرض Logo و Cover */}
                            {(branchDetails.logo || branchDetails.cover) && (
                                <div className="col-12 mb-4">
                                    <div className="row">
                                        {/* Logo */}
                                        {branchDetails.logo && branchDetails.logo !== 'logo' && (
                                            <div className="col-md-6 mb-3">
                                                <div className="d-flex align-items-center mb-2">
                                                    <FaStore className="text-primary mx-2" />
                                                    <h6 className="mb-0 text-muted">شعار الفرع (Logo)</h6>
                                                </div>
                                                <div style={{
                                                    width: '200px',
                                                    height: '200px',
                                                    border: '2px solid #e9ecef',
                                                    borderRadius: '12px',
                                                    overflow: 'hidden',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                }}>
                                                    <img
                                                        src={branchDetails.logo}
                                                        alt="Branch Logo"
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Cover */}
                                        {branchDetails.cover && branchDetails.cover !== 'cover' && (
                                            <div className="col-md-12 mb-3">
                                                <div className="d-flex align-items-center mb-2">
                                                    <h6 className="mb-0 text-muted">🖼️ صورة الغلاف (Cover)</h6>
                                                </div>
                                                <div style={{
                                                    width: '100%',
                                                    maxWidth: '800px',
                                                    height: '300px',
                                                    border: '2px solid #e9ecef',
                                                    borderRadius: '12px',
                                                    overflow: 'hidden',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                }}>
                                                    <img
                                                        src={branchDetails.cover}
                                                        alt="Branch Cover"
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <hr className="my-4" />
                                </div>
                            )}

                            {/* Basic Info */}
                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaStore className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">اسم الفرع</h6>
                                </div>
                                <p className="fw-bold">{branchDetails.market_name || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaStore className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">الصيدلية الرئيسية</h6>
                                </div>
                                <p className="fw-bold">{branchDetails.parent_name || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaUser className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">اسم التاجر</h6>
                                </div>
                                <p className="fw-bold">{branchDetails.trader_name || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaPhone className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">رقم الهاتف</h6>
                                </div>
                                <p className="fw-bold">{branchDetails.phone || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaPhone className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">رقم التواصل</h6>
                                </div>
                                <p className="fw-bold">{branchDetails.contact_number || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaEnvelope className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">البريد الإلكتروني</h6>
                                </div>
                                <p className="fw-bold">{branchDetails.email || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaMapMarkerAlt className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">العنوان</h6>
                                </div>
                                <p className="fw-bold">{branchDetails.address || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaClock className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">مواعيد العمل</h6>
                                </div>
                                <p className="fw-bold">
                                    {branchDetails.start_time && branchDetails.end_time 
                                        ? `من ${branchDetails.start_time} إلى ${branchDetails.end_time}`
                                        : 'غير محدد'
                                    }
                                </p>
                            </div>

                            {branchDetails.lat && branchDetails.lng && (
                                <div className="col-md-12 mb-3">
                                    <div className="d-flex align-items-center mb-2">
                                        <FaMapMarkerAlt className="text-primary mx-2" />
                                        <h6 className="mb-0 text-muted">الموقع على الخريطة</h6>
                                    </div>
                                    <div style={{ height: '350px', borderRadius: '8px', overflow: 'hidden', border: '2px solid #e9ecef', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                        <iframe
                                            title="موقع الفرع"
                                            width="100%"
                                            height="100%"
                                            frameBorder="0"
                                            style={{ border: 0 }}
                                            src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyAHQSuklfkVa-mATYvbA7odk3_LpnKG-OA&q=${parseFloat(branchDetails.lat).toFixed(7)},${parseFloat(branchDetails.lng).toFixed(7)}&zoom=18&language=ar`}
                                            allowFullScreen>
                                        </iframe>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mt-2">
                                        <small className="text-muted">
                                            الإحداثيات: {parseFloat(branchDetails.lat).toFixed(7)}, {parseFloat(branchDetails.lng).toFixed(7)}
                                        </small>
                                        <a 
                                            href={`https://www.google.com/maps?q=${parseFloat(branchDetails.lat).toFixed(7)},${parseFloat(branchDetails.lng).toFixed(7)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm text-white"
                                            style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                                            فتح في Google Maps
                                        </a>
                                    </div>
                                </div>
                            )}

                            <div className="col-md-6 mb-3">
                                <h6 className="text-muted mb-2">حالة النشاط</h6>
                                {getStatusBadge(branchDetails.status)}
                            </div>

                            <div className="col-md-6 mb-3">
                                <h6 className="text-muted mb-2">حالة الحظر</h6>
                                {getBanBadge(branchDetails.ban)}
                            </div>

                            {branchDetails.description && (
                                <div className="col-md-12 mb-3">
                                    <h6 className="text-muted mb-2">الوصف</h6>
                                    <p className="fw-bold">{branchDetails.description}</p>
                                </div>
                            )}

                            {branchDetails.rate_avg && (
                                <div className="col-md-6 mb-3">
                                    <h6 className="text-muted mb-2">التقييم</h6>
                                    <p className="fw-bold">{branchDetails.rate_avg} ⭐</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminPharmacyBranchDetails;

